import React from "react";
import Button from "@amzn/awsui-components-react/polaris/button";
import { TopNavigationProps } from "@amzn/awsui-components-react/polaris/top-navigation/interfaces";
import { useNavigate } from "react-router-dom";
import {
  AgenticTranslate,
  useAgenticTranslator,
} from "../i18n/agenticTranslator";

/**
 * Pathname prefix that identifies the Agentic_AAP route tree
 * (R1.1 — `/services/agentic`).
 */
export const AGENTIC_PATH_PREFIX = "/services/agentic";

/**
 * Pathname target when leaving Agentic_AAP and returning to Classic_AAP
 * (R1.4).
 */
export const CLASSIC_PATH_TARGET = "/services";

/**
 * Translation key used WHILE the user is in Classic_AAP — the toggle
 * advertises a switch *to* Agentic_AAP (R1.3).
 */
export const KEY_TOGGLE_TO_AGENTIC = "agentic_toggle_to_agentic_label";

/**
 * Translation key used WHILE the user is in Agentic_AAP — the toggle
 * advertises a switch *back* to Classic_AAP (R1.4).
 */
export const KEY_TOGGLE_TO_CLASSIC = "agentic_toggle_to_classic_label";

/**
 * `true` when `pathname` matches `/services/agentic` or any descendant.
 *
 * Per design §J the toggle uses a path-prefix regex rather than an exact
 * match so that nested Agentic_AAP routes (e.g. `/services/agentic/foo`)
 * also resolve to the "Back to Classic AAP" label. The expression is
 * anchored at the start of the string and requires either end-of-string
 * or a trailing `/` to avoid matching e.g. `/services/agentic-other`.
 */
export const isAgenticPathname = (pathname: string): boolean =>
  /^\/services\/agentic(?:$|\/)/.test(pathname);

/**
 * Pure function — given a pathname, returns the translation key for the
 * toggle's visible label / `aria-label` and the navigation target the
 * button should activate.
 *
 * Implements:
 * - R1.3 (Classic → Agentic label + target)
 * - R1.4 (Agentic → Classic label + target)
 */
export const resolveToggleTarget = (
  pathname: string
): { labelKey: string; target: string } => {
  if (isAgenticPathname(pathname)) {
    return {
      labelKey: KEY_TOGGLE_TO_CLASSIC,
      target: CLASSIC_PATH_TARGET,
    };
  }
  return {
    labelKey: KEY_TOGGLE_TO_AGENTIC,
    target: AGENTIC_PATH_PREFIX,
  };
};

/**
 * Toggle_Button — design §J, requirements R1.2–R1.4, R1.9, R15.3.
 *
 * Rendered as a Polaris `<Button>` (variant `"link"`) so it can stand in
 * as a fallback / standalone control where a `TopNavigation` utility
 * cannot be placed (e.g. inside the Agentic chat header in tests). The
 * canonical mounting path is the `TopNavigation.utilities` array via
 * `buildAgenticToggleUtility` (HeaderLayout injection lands in task 53).
 *
 * Behavior:
 * - Reads `window.location.pathname` at render time and resolves both
 *   the visible label and the navigation target via
 *   `resolveToggleTarget`.
 * - On activation (click / Enter / Space — keyboard accessible by
 *   virtue of the underlying `<button>` element, R1.9 / R15.3) calls
 *   `react-router-dom`'s programmatic `useNavigate()` with the resolved
 *   target. Programmatic navigation preserves the active locale, the
 *   authenticated session, and the captured CSRF token (R1.7).
 * - The `aria-label` is set to the same translated string as the visible
 *   text (R1.9).
 *
 * Notes:
 * - The component reads `window.location.pathname` directly rather than
 *   `useLocation()` because it is also used outside a `<Routes>` /
 *   `<Outlet>` boundary in some test scenarios — the pathname read is
 *   identical in either case.
 * - The component intentionally does NOT depend on
 *   `react-router-dom`'s `<Link>` so it can render as a button (per
 *   `TopNavigation.utilities` button-utility shape) instead of an
 *   anchor.
 */
const ToggleButton: React.FC = () => {
  const t = useAgenticTranslator();
  const navigate = useNavigate();
  const { labelKey, target } = resolveToggleTarget(window.location.pathname);
  const label = t(labelKey);
  return (
    <Button
      variant="link"
      ariaLabel={label}
      onClick={() => navigate(target)}
    >
      {label}
    </Button>
  );
};

export default ToggleButton;

/**
 * Factory returning a Polaris `TopNavigation.utilities` button entry for
 * the Toggle_Button.
 *
 * Designed to be called once per `HeaderLayout` render so the
 * `text` / `ariaLabel` / `onClick.target` pair always reflect the
 * current `pathname`. The injection itself lands in task 53.
 *
 * @param translate Translator function (typically obtained via
 *   `useAgenticTranslator()` or `useTranslator().translate`).
 * @param navigate `react-router-dom` navigate function.
 * @param pathname Pathname to resolve against. Defaults to
 *   `window.location.pathname`. Exposed primarily for unit testing —
 *   in production, `HeaderLayout` calls the factory once per render and
 *   the default is correct.
 *
 * Implements:
 * - R1.2 (utility entry alongside locale + user-profile dropdowns)
 * - R1.3 (Classic → Agentic label + target)
 * - R1.4 (Agentic → Classic label + target)
 * - R1.9 (`aria-label` resolved from the same translation key)
 * - R15.3 (keyboard activatable — Polaris button utility is rendered
 *   as a native `<button>`)
 */
export const buildAgenticToggleUtility = (
  translate: AgenticTranslate,
  navigate: (path: string) => void,
  pathname: string = window.location.pathname
): TopNavigationProps.ButtonUtility => {
  const { labelKey, target } = resolveToggleTarget(pathname);
  const label = translate(labelKey);
  return {
    type: "button",
    variant: "link",
    text: label,
    ariaLabel: label,
    onClick: () => navigate(target),
  };
};
